/**
 * Den Ort halten, wenn sich im Verlauf etwas ändert, das nicht der Leser ist.
 * (01.10.2026, Ticket 2873)
 *
 * Martin, iPhone: *„Wenn ich hin- und herscrolle, dann springt das
 * unkontrolliert nach unten oder nach oben."* Und: *„Auch im Chatverlauf
 * springt es sehr eigenartig herum."*
 *
 * ## Was gemessen wurde
 *
 * Chrome hält den Ort von selbst (`overflow-anchor`): Wird über dem Sichtfeld
 * etwas höher oder niedriger, rechnet der Browser `scrollTop` nach. WebKit
 * (iPhone, Safari, die App) kennt das nicht — dort steht `scrollTop` still und
 * der Inhalt rutscht unter dem Finger. Im Nachbau mit abgeschalteter
 * Verankerung (`#chat { overflow-anchor: none }`, Chromium, 393 × 852) schob
 * allein das Nachladen älterer Nachrichten die Bezugsblase im Sichtfeld um
 * 7.085 Punkte in EINEM Bild; die Ansicht flog danach in Schritten von rund
 * 300 Punkten (weiches Scrollen, `scroll-behavior: smooth`) 3.500 Punkte nach
 * unten. Dieselbe Form wie im Journal vom 01.10.: 249, 294, 338, 381 Punkte
 * je Ereignis, jedes Mal rund 44 mehr — die Beschleunigung einer weichen
 * Scroll-Animation, kein Finger.
 *
 * ## Die Regel
 *
 * Wer den Verlauf von außen verändert (Blasen oben einfügen, lange Blasen
 * zuklappen, den Verlauf neu aufbauen), macht es so:
 *
 *   1. Ort nehmen — steht der Leser am Ende (≤ 4 Punkte), merkt sich der Ort
 *      den Abstand zum Ende; sonst die erste Blase, die im Sichtfeld oder
 *      darunter beginnt, samt ihrem Abstand zum oberen Rand.
 *   2. Ändern.
 *   3. Ort halten — `scrollTop` so setzen, dass derselbe Abstand wieder
 *      herrscht. Sofort, im selben Schritt: kein Bild dazwischen, keine
 *      Animation, die ein veraltetes Ziel ansteuert.
 *
 * ⚠️ Das gilt in Chrome genauso: Die Rechnung findet nach der Verankerung des
 *    Browsers nichts mehr zu tun (Unterschied null) und fasst nichts an.
 *
 * ⚠️ Ein Tagestrenner ist NIE ein Anker (`KEIN_ANKER`). Er gehört dem Tag,
 *    nicht der Stelle: kommen ältere Blasen davor, wird er umbeschriftet,
 *    entfernt oder neu gesetzt (`refreshDayDividers` in app.ts) — und
 *    steht ganz oben, wenn der Leser am Anfang des Verlaufs steht (Wisch mit
 *    Schwung, Pos1, Tipp auf die Statusleiste: `scrollTop` 0). Dann war er das
 *    erste sichtbare Kind, die älteren Blasen kamen UNTER ihn, er bewegte sich
 *    nicht — und die Rechnung fand nichts zu tun: der Leser landete am Anfang
 *    der 80 neu geladenen Nachrichten (Nachbau: Bezugsblase 19.097 Punkte
 *    weg). Zweiter Teil derselben Regel: Die Trenner müssen im SELBEN Schritt
 *    entstehen wie die Blasen (app.ts), sonst schiebt jeder neue Tag den Leser
 *    nachträglich um seine Höhe (gemessen ohne Verankerung: 196 Punkte bei
 *    fünf Tagen).
 *
 * ⚠️ Gemessen wird nur über `getBoundingClientRect` — nie über `offsetTop`.
 *    Der Verlauf trägt `transform: translateZ(0)`; was dann der `offsetParent`
 *    ist, hängt vom Browser ab.
 */

import { schreibeScrollTop } from "./scroll-ruhe.js";

/** So nah am Ende (Punkte) gilt der Verlauf als „am Ende“ — dasselbe Maß wie `AM_ENDE_PX` in lib/chat-folgen.ts. */
export const ENDE_NAH_PX = 4;

/**
 * Diese Kinder des Verlaufs sind nie der Anker: Ein Tagestrenner wird beim
 * Nachladen umbeschriftet, entfernt oder neu gesetzt und bewegt sich darum
 * nicht mit dem Inhalt, den der Leser sieht.
 */
export const KEIN_ANKER = ".chat-daysep";

/** So lange nach dem Halten erkennt die Wache ihr Scroll-Ereignis als „vom Programm gehalten“ wieder. */
export const GEHALTEN_MS = 500;
/**
 * So weit darf die Fläche beim Scroll-Ereignis von der Stelle des Haltens entfernt stehen: Ein Finger oder
 * Schwung kann im selben Bild noch dazukommen (gemessen höchstens rund 80 Punkte je Bild; Nachladen bei
 * aufliegendem Finger war genau der Fall mit `finger=ja delta=+18228`). Ein Sprung ≥ 220 woanders bleibt einer.
 */
export const GEHALTEN_TOLERANZ_PX = 150;

/** Eine Blase und ihr Abstand zum oberen Rand des Verlaufs, wie sie vor der Änderung stand. */
export interface Anker {
  el: Element;
  versatz: number;
}

export interface Ort {
  amEnde: boolean;
  abstandEnde: number;
  anker: Anker | null;
}

/** Reine Rechnung: wohin muss `scrollTop`, damit der Anker wieder an seinem Platz steht? */
export function obenFuerAnker(oben: number, versatzVorher: number, versatzJetzt: number): number {
  return oben + (versatzJetzt - versatzVorher);
}

/** Reine Rechnung: `scrollTop`, das denselben Abstand zum Ende wahrt. */
export function obenAmEnde(scrollHoehe: number, sichtHoehe: number, abstandEnde: number): number {
  return Math.max(0, scrollHoehe - sichtHoehe - abstandEnde);
}

/** Wo steht der Leser? Nur Lesen, nichts ändern. */
export function ortNehmen(chat: HTMLElement): Ort {
  const abstandEnde = chat.scrollHeight - chat.scrollTop - chat.clientHeight;
  if (abstandEnde <= ENDE_NAH_PX) return { amEnde: true, abstandEnde, anker: null };
  const kasten = chat.getBoundingClientRect();
  for (const kind of Array.from(chat.children)) {
    if (kind.matches(KEIN_ANKER)) continue;
    const r = kind.getBoundingClientRect();
    if (r.bottom > kasten.top + 1) {
      return { amEnde: false, abstandEnde, anker: { el: kind, versatz: r.top - kasten.top } };
    }
  }
  return { amEnde: false, abstandEnde, anker: null };
}

/**
 * `scrollTop` zurechtrücken. Gibt zurück, um wie viele Punkte es sich bewegt
 * hat (0: nichts zu tun — etwa, weil der Browser selbst verankert hat).
 */
export function ortHalten(chat: HTMLElement, ort: Ort): number {
  let ziel: number | null = null;
  if (ort.amEnde) {
    ziel = obenAmEnde(chat.scrollHeight, chat.clientHeight, ort.abstandEnde);
  } else if (ort.anker?.el.isConnected) {
    const jetzt = ort.anker.el.getBoundingClientRect().top - chat.getBoundingClientRect().top;
    ziel = obenFuerAnker(chat.scrollTop, ort.anker.versatz, jetzt);
  }
  if (ziel === null) return 0;
  const vorher = chat.scrollTop;
  if (Math.abs(ziel - vorher) < 0.5) {
    // Nichts zu schreiben (der Browser hat selbst verankert): kein Schreiben —
    // es würde auf dem iPhone ein laufendes Auslaufen abbrechen. Das
    // Scroll-Ereignis des Browsers ist trotzdem „gehalten“.
    merkeGehalten(chat);
    return 0;
  }
  // Sofort: `.chat` scrollt nicht mehr weich (style.css) — eine Animation würde
  // ein Ziel ansteuern, das beim nächsten Bild schon überholt ist. Über die
  // gemeinsame Regel (lib/scroll-ruhe.ts): Schreibt hier jemand mitten in einer
  // Bewegung, steht es als `schreiber=scroll-ort` in der Takt-Zeile. Wer den
  // Verlauf ändert, wartet vorher auf die Ruhe (Klapper, Nachladen in app.ts).
  schreibeScrollTop(chat, ziel, "scroll-ort", true);
  merkeGehalten(chat);
  return chat.scrollTop - vorher;
}

/*
 * Hält der Browser den Ort selbst? (02.10.2026, pre.3612)
 *
 * Martin, iPhone, 22:25: Beim schnellen Hochwischen fehlten Blasen, „sie tauchen erst auf, wenn der Verlauf
 * stehen bleibt“. Seit pre.3607 wartete das Nachladen Älterer, bis er die Fläche losließ — gemessen im echten
 * WebKit (Fall h): 1.157 ms stand er oben am Anfang, bevor das Ältere da war (300 ms davon Netz). Das Warten
 * gibt es nur, damit nicht WIR `scrollTop` mitten in den Wisch schreiben. WebKit 26.5 hält den Ort aber selbst
 * (`overflow-anchor`, gemessen: synchron beim nächsten Layout, um genau die eingefügte Höhe; nur bei
 * `scrollTop` 0 nicht) — dann findet `ortHalten` nichts zu tun und schreibt nichts. In dem Fall wird sofort
 * eingefügt, auch während er wischt.
 *
 * ⚠️ Gemessen im echten Verlauf der App (WebKit 26.5, `DBG_ANKER=1` in tests/browser/scroll-iphone-webkit.mts):
 *    dort verankerte WebKit beim Nachladen NICHT (`ortHalten` schrieb 1.000 → 18.794), während eine schlichte
 *    Probe-Liste verankert. Ob und wann das iPhone verankert, ist auf dem Server nicht prüfbar. Darum lernt die
 *    Regel nur in Ruhe: Erst wenn ein Einfügen OHNE Bewegung zeigt, dass `ortHalten` nichts zu tun hatte, wird
 *    künftig sofort eingefügt; musste es schreiben, bleibt es beim Warten. So schreibt das Lernen selbst nie
 *    in einen Wisch.
 */
let browserVerankert: boolean | null = null;

/** Darf jetzt über dem Leser eingefügt werden, ohne dass wir `scrollTop` schreiben müssen? */
export function browserHaeltOrt(chat: HTMLElement): boolean {
  if (browserVerankert !== true || chat.scrollTop <= 0) return false;
  try {
    return CSS.supports("overflow-anchor", "auto") && getComputedStyle(chat).overflowAnchor !== "none";
  } catch {
    return false;
  }
}

/**
 * Nach einem Einfügen über dem Leser (es kam wirklich etwas dazu): hat der Browser verankert? `verschoben` = was
 * `ortHalten` noch tun musste. Ein einziges Mal „nein“ gilt für die ganze Sitzung.
 */
export function merkeVerankerung(verschoben: number): void {
  if (Math.abs(verschoben) >= 2) browserVerankert = false;
  else browserVerankert ??= true;
}

/** Was die Regel bisher weiß (`null`: noch nie in Ruhe eingefügt). Für Tests und die Takt-Zeile. */
export function verankerungBekannt(): boolean | null {
  return browserVerankert;
}

/** Nur für Tests. */
export function verankerungZuruecksetzen(): void {
  browserVerankert = null;
}

/*
 * Das Scroll-Ereignis, das aus dem Ort-Halten folgt, ist kein Sprung — der Inhalt
 * hat sich um genau diesen Betrag verschoben, der Leser sieht nichts. Ohne diese
 * Marke schrieb die Scroll-Wache nach jedem Nachladen `art=sprung delta=+18228` —
 * Rauschen, das aussieht wie der ursprüngliche Fehler.
 */
const gehaltenAn = new WeakMap<Element, { oben: number; t: number }>();

function merkeGehalten(chat: HTMLElement): void {
  gehaltenAn.set(chat, { oben: chat.scrollTop, t: Date.now() });
}

/**
 * Steht diese Fläche jetzt dort, wo das Ort-Halten sie eben hingestellt hat
 * (binnen `GEHALTEN_MS`, auf `GEHALTEN_TOLERANZ_PX` genau)? Dann gehört das
 * Scroll-Ereignis dem Programm, nicht dem Leser. Ist er danach weiter gescrollt
 * oder springt etwas anderes, stimmt der Ort nicht mehr.
 */
export function warGehalten(el: Element, oben: number, jetzt: number): boolean {
  const g = gehaltenAn.get(el);
  return g !== undefined && jetzt - g.t >= 0 && jetzt - g.t <= GEHALTEN_MS && Math.abs(oben - g.oben) <= GEHALTEN_TOLERANZ_PX;
}

/**
 * Eine Änderung am Verlauf vornehmen, ohne dass der Leser etwas davon merkt.
 *
 * Ist der Verlauf nicht sichtbar (Ansicht versteckt, Höhe 0), gibt es keinen
 * Ort zu halten — die Änderung läuft einfach.
 */
export function haltenOhneSprung(chat: HTMLElement, aenderung: () => void): number {
  if (chat.clientHeight === 0) {
    aenderung();
    return 0;
  }
  const ort = ortNehmen(chat);
  aenderung();
  return ortHalten(chat, ort);
}

/*
 * Ein Seitenwechsel ist kein Sprung. (02.10.2026, Ticket 2912)
 *
 * Tippt Martin in den Einstellungen eine Zeile an, baut `SettingsView.zeigeSeite` den Körper neu
 * und stellt `scrollTop` mit Absicht auf 0 — bei einer kurzen Seite fällt die Höhe dabei von
 * 1.528 auf unter 700. Die Wache schrieb dazu `art=sprung delta=-758 … ausloeser=-p.einst-zustand…`
 * (Journal 02.10., 07:05:25) — und niemand konnte sagen, ob das der Fehler war oder das Antippen.
 * Nachbau (Chromium, echte Einstellungen, echte Wache): dieselbe Zeile, Zeichen für Zeichen, bei
 * einem ganz normalen Tipp. Mit dieser Marke bleibt die Zeile den echten Sprüngen vorbehalten.
 */
const SEITENWECHSEL_MS = 600;

const wechselAn = new WeakMap<Element, number>();

/** Das Programm tauscht gleich den ganzen Inhalt dieser Fläche aus und stellt sie auf den Anfang. */
export function merkeSeitenwechsel(el: Element): void {
  wechselAn.set(el, Date.now());
}

/** Hat das Programm diese Fläche eben (binnen `SEITENWECHSEL_MS`) auf neuen Inhalt gestellt? */
export function warSeitenwechsel(el: Element, jetzt: number): boolean {
  const t = wechselAn.get(el);
  return t !== undefined && jetzt - t >= 0 && jetzt - t <= SEITENWECHSEL_MS;
}
